import React, { useState } from 'react';
import { Modal, Tag, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { IMultiEnabled } from '../../interfaces';
import { sortTextArrayByDict, subscribersFeaturesDict, upperCaseFirstLetter } from '../../instruments';
import { ITrCustom, tB, tCustom, tI } from '../../translate';
import Styles from '../styles/m_itemCardStyles.less';

type IMultiTadigShowField = {
    children?: never;
    itemName?: string;
    onToggle?: (feature: string, enabled: boolean) => Promise<void> | void;
    values?: IMultiEnabled;
};

export const MultiFeaturesShowTags: React.FC<IMultiTadigShowField> = ({
    itemName,
    onToggle,
    values = {},
}: IMultiTadigShowField) => {
    const [togglingFeature, setTogglingFeature] = useState<string>('');
    const names: Array<string> = sortTextArrayByDict(Object.keys(values), subscribersFeaturesDict);

    const confirmToggle = (feature: string, isOn: boolean): void => {
        const nextEnabled = !isOn;
        const params = { feature: upperCaseFirstLetter(feature), subscriber: itemName };
        const titleKey: ITrCustom = nextEnabled ? 'Enable feature' : 'Disable feature';
        const contentKey: ITrCustom = nextEnabled ? 'Enable feature for subscriber' : 'Disable feature for subscriber';

        Modal.confirm({
            title: tCustom(titleKey, params),
            content: itemName ? tCustom(contentKey, params) : undefined,
            okText: tB('Yes'),
            cancelText: tB('No'),
            okButtonProps: { danger: !nextEnabled },
            wrapClassName: 'modal',
            onOk: async () => {
                setTogglingFeature(feature);
                try {
                    await onToggle?.(feature, nextEnabled);
                } finally {
                    setTogglingFeature('');
                }
            },
        });
    };

    return (
        <span className={Styles.tadigBox}>
            {names.map((item: string) => {
                const text = upperCaseFirstLetter(item);
                const isOn = !!values?.[item].enabled;
                const isToggling = togglingFeature === item;
                const stateText = `${text}: ${isOn ? tI('On') : tI('Off')}`;
                const toggleText = (isOn ? tB('Disable') : tB('Enable')).toLowerCase();

                return (
                    <Tooltip key={item} title={onToggle ? `${stateText} — ${toggleText}` : stateText}>
                        <Tag
                            icon={isOn ? <CheckOutlined /> : <CloseOutlined />}
                            className={cx(
                                Styles.tadig,
                                isOn ? Styles.tadigEnable : Styles.tadigDisable,
                                onToggle && Styles.tadigClickable,
                                isToggling && Styles.tadigLoading,
                            )}
                            onClick={onToggle && !isToggling ? (): void => confirmToggle(item, isOn) : undefined}
                        >
                            {text}
                        </Tag>
                    </Tooltip>
                );
            })}
        </span>
    );
};
